import { useEffect, useState, type FormEvent } from 'react';
import {
  createInvitation,
  fetchInvitations,
  revokeInvitation,
  type InvitationView,
} from '../../api/admin';
import { ca } from '../../i18n/ca';
import { errorText, fmtWhen } from './shared';

interface Props {
  onError: (text: string | null) => void;
  onNotice: (text: string | null) => void;
}

/**
 * Invitations tab: one link per person or per group. The code is generated
 * here and travels only inside the address the admin copies, so nobody has to
 * share one code with everyone (which is what the single configured code was).
 */
export function InvitationsTab({ onError, onNotice }: Props) {
  const [list, setList] = useState<InvitationView[]>([]);
  const [note, setNote] = useState('');
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInDays, setExpiresInDays] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchInvitations()
      .then(setList)
      .catch((err) => onError(errorText(err)));
  }, [onError]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const created = await createInvitation({ note, maxUses, expiresInDays });
      setList((current) => [created, ...current]);
      setNote('');
      onError(null);
      await copy(created.url);
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      onNotice(ca.adminUi.inviteCopied);
    } catch {
      onNotice(ca.adminUi.inviteCopyFailed);
    }
  }

  async function revoke(id: number) {
    try {
      const updated = await revokeInvitation(id);
      setList((current) => current.map((i) => (i.id === id ? updated : i)));
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  return (
    <section className="stack" aria-label={ca.adminUi.tabs.invitations}>
      <p className="muted small">{ca.adminUi.intros.invitations}</p>
      <form className="card form" onSubmit={create} aria-label={ca.adminUi.inviteNew}>
        <h2>
          <i className="bi bi-envelope-paper" aria-hidden="true" /> {ca.adminUi.inviteNew}
        </h2>
        <p className="muted small">{ca.adminUi.inviteNewIntro}</p>
        <label>
          {ca.adminUi.inviteNote}
          <input
            name="note"
            maxLength={120}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <span className="hint">{ca.adminUi.inviteNoteHint}</span>
        </label>
        <div className="row invite-numbers">
          <label>
            {ca.adminUi.inviteMaxUses}
            <input
              name="maxUses"
              type="number"
              min={0}
              max={1000}
              value={maxUses}
              onChange={(e) => setMaxUses(Number(e.target.value))}
            />
            <span className="hint">{ca.adminUi.inviteMaxUsesHint}</span>
          </label>
          <label>
            {ca.adminUi.inviteExpiresIn}
            <input
              name="expiresInDays"
              type="number"
              min={0}
              max={365}
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
            />
            <span className="hint">{ca.adminUi.inviteExpiresInHint}</span>
          </label>
        </div>
        <div className="row actions">
          <button type="submit" className="primary" disabled={busy}>
            <i className="bi bi-link-45deg" aria-hidden="true" /> {ca.adminUi.inviteCreate}
          </button>
        </div>
      </form>
      {list.length === 0 ? (
        <p className="muted small">{ca.adminUi.inviteNone}</p>
      ) : (
        <div className="table-wrap">
          <table className="data invitations">
            <thead>
              <tr>
                <th>{ca.adminUi.colInviteFor}</th>
                <th>{ca.adminUi.inviteLink}</th>
                <th>{ca.adminUi.colInviteUses}</th>
                <th>{ca.adminUi.colInviteExpires}</th>
                <th>{ca.adminUi.colState}</th>
                <th>{ca.adminUi.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((invitation) => (
                <tr key={invitation.id}>
                  <td>{invitation.note || '—'}</td>
                  <td>
                    <code className="small">{invitation.url}</code>
                  </td>
                  <td>{ca.adminUi.inviteUses(invitation.uses, invitation.maxUses)}</td>
                  <td>
                    {invitation.expiresAt ? fmtWhen(invitation.expiresAt) : ca.adminUi.inviteNever}
                  </td>
                  <td>
                    <span className={`chip ${invitation.usable ? 'ok' : 'warn'}`}>
                      {stateOf(invitation)}
                    </span>
                  </td>
                  <td>
                    <div className="row actions">
                      <button type="button" onClick={() => void copy(invitation.url)}>
                        <i className="bi bi-clipboard" aria-hidden="true" /> {ca.adminUi.inviteCopy}
                      </button>
                      {!invitation.revoked && (
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => void revoke(invitation.id)}
                        >
                          {ca.adminUi.inviteRevoke}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Why an invitation opens nothing any more, when it does not: withdrawn, spent, or past its date. */
function stateOf(invitation: InvitationView): string {
  if (invitation.usable) return ca.adminUi.inviteActive;
  if (invitation.revoked) return ca.adminUi.inviteRevoked;
  if (invitation.expiresAt && new Date(invitation.expiresAt) < new Date()) {
    return ca.adminUi.inviteExpired;
  }
  return ca.adminUi.inviteSpent;
}
